import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { TableRowData } from '../../../../../types/common.types';
import { Member, Team, TeamMember, TeamMemberExcelFailure } from '../../../../../types/entity.types';

import { ARCHIVED } from '../../../../../utils/constants';
import { getClassNames } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import FilePicker from '../../../../common/input/file-picker/FilePicker';
import Table from '../../../../common/table/Table';
import AddTeamMemberModal from '../team-modals/add-team-member-modal/AddTeamMemberModal';
import AddTeamModal from '../team-modals/add-team-modal/AddTeamModal';
import EditTeamModal from '../team-modals/edit-team-modal/EditTeamModal';
import RemoveTeamMemberModal from '../team-modals/remove-team-member-modal/RemoveTeamMemberModal';
import TeamUploadFailureModal from '../team-modals/team-upload-failure-modal/TeamUploadFailureModal';

import './team-table.scss';

type TeamDataTemplate = {
  teamName: string;
  numberOfMembers: string;
};

type TeamMembersDataTemplate = {
  teamMember: string;
  userName: string;
};

const TEAMS_DATA_TEMPLATE: TeamDataTemplate = {
  teamName: '',
  numberOfMembers: ''
};

const TEAMS_HIDDEN_COLUMNS = ['externalReference'];

const TEAM_MEMBERS_DATA_TEMPLATE: TeamMembersDataTemplate = {
  teamMember: '',
  userName: ''
};

const TEAM_MEMBERS_HIDDEN_COLUMNS = ['memberExternalReference'];

type Props = {
  team?: Team;
  teams: Team[];
  members: Member[];
  createTeam: (team: Team, onSuccess: () => void) => void;
  updateTeam: (team: Team, onSuccess: () => void) => void;
  deleteTeam: (onSuccess: () => void) => void;
  addMemberToTeam: (memberExternalReference: string, close: () => void) => void;
  removeMemberFromTeam: (memberExternalReference: string, p: () => void) => void;
  uploadTeamExcel: (file: File, onError: (value: TeamMemberExcelFailure[]) => void) => void;
  downloadTeamExcel: () => void;
};

const TeamTable = ({
  team,
  teams,
  members,
  createTeam,
  updateTeam,
  deleteTeam,
  addMemberToTeam,
  removeMemberFromTeam,
  uploadTeamExcel,
  downloadTeamExcel
}: Props) => {
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | undefined>();
  const [teamUploadFailures, setTeamUploadFailures] = useState<TeamMemberExcelFailure[] | undefined>();

  const fileRef = useRef<HTMLInputElement | null>(null);

  const { status } = useParams();

  const navigate = useNavigate();

  const sortedTeams = useMemo(
    () =>
      [...teams].sort((teamOne, teamTwo): number => {
        if (!teamOne.teamName || !teamTwo.teamName) return 0;

        if (teamOne.teamName > teamTwo.teamName) return 1;
        if (teamOne.teamName < teamTwo.teamName) return -1;
        return 0;
      }),
    [teams]
  );

  const sortedMembers = useMemo(() => {
    if (!team) return [];

    return [...(team.teamMembers ?? [])].sort((memberOne, memberTwo) => {
      if (!memberOne.teamMember && !memberTwo.teamMember) return 0;

      if (!memberOne.teamMember) return -1;

      if (!memberTwo.teamMember) return 1;

      if (memberOne.teamMember.toLowerCase() > memberTwo.teamMember.toLowerCase()) return 1;

      if (memberOne.teamMember.toLowerCase() < memberTwo.teamMember.toLowerCase()) return -1;
      return 0;
    });
  }, [team]);

  const [searchParams] = useSearchParams();

  const handleDeleteClick = () => {
    if (!memberToRemove?.memberExternalReference) return;
    removeMemberFromTeam(memberToRemove?.memberExternalReference, () => setMemberToRemove(undefined));
  };

  const handleTeamClick = (team: TableRowData) => {
    let to = `${team.externalReference}`;
    if (searchParams.get('hasSubProgrammes') === 'true') {
      to = `${to}?hasSubProgrammes=true`;
    }

    navigate(to);
  };

  const renderTopActions = () => {
    return (
      <div className="team-table__top-actions">
        {team ? (
          <p className="team-table__top-actions__message team-table__top-actions__message--team">{team.teamName}</p>
        ) : (
          <p className="team-table__top-actions__message">Click on a team to see members</p>
        )}
      </div>
    );
  };

  const renderBottomActions = () => {
    if (team) {
      return (
        <div className="team-table__bottom-actions">
          <Button text="Edit Team" onClick={() => setShowEditTeamModal(true)} />
          <Button text="Add Team Member" onClick={() => setShowAddTeamMemberModal(true)} />
        </div>
      );
    }

    return (
      <div className="team-table__bottom-actions">
        {status !== ARCHIVED && (
          <>
            <Button text="Add Team" onClick={() => setShowAddTeamModal(true)} />
            <FilePicker
              triggerElement={<Button text="Upload .xlsx" onClick={() => fileRef?.current?.click()} />}
              fileRef={fileRef}
              onChange={({ target }) => target.files?.[0] && uploadTeamExcel(target.files[0], setTeamUploadFailures)}
            />
          </>
        )}
        <Button text="Download .xlsx" onClick={downloadTeamExcel} />
      </div>
    );
  };

  return (
    <>
      <section className={getClassNames('team-table', { team })} data-testid="team-table">
        {team ? (
          <Table
            data={sortedMembers}
            dataTemplate={TEAM_MEMBERS_DATA_TEMPLATE}
            hiddenColumns={TEAM_MEMBERS_HIDDEN_COLUMNS}
            rowsPerPage={10}
            onDeleteRowClick={(rowData) => setMemberToRemove(rowData)}
            topActions={renderTopActions()}
            bottomActions={renderBottomActions()}
            filter
          />
        ) : (
          <Table
            data={sortedTeams}
            dataTemplate={TEAMS_DATA_TEMPLATE}
            hiddenColumns={TEAMS_HIDDEN_COLUMNS}
            rowsPerPage={10}
            handleRowClick={handleTeamClick}
            topActions={renderTopActions()}
            bottomActions={renderBottomActions()}
            filter
            onDeleteRowClick={undefined}
          />
        )}
      </section>

      <AddTeamModal
        show={showAddTeamModal}
        close={() => setShowAddTeamModal(false)}
        members={members}
        createTeam={createTeam}
      />

      <EditTeamModal
        show={showEditTeamModal}
        close={() => setShowEditTeamModal(false)}
        team={team}
        updateTeam={updateTeam}
        deleteTeam={deleteTeam}
      />

      <AddTeamMemberModal
        show={showAddTeamMemberModal}
        close={() => setShowAddTeamMemberModal(false)}
        members={members}
        teamMembers={team?.teamMembers}
        addMemberToTeam={addMemberToTeam}
      />

      <RemoveTeamMemberModal
        show={!!memberToRemove}
        close={() => setMemberToRemove(undefined)}
        onDeleteClick={() => handleDeleteClick()}
      />

      <TeamUploadFailureModal
        show={!!teamUploadFailures}
        close={() => setTeamUploadFailures(undefined)}
        failures={teamUploadFailures}
      />
    </>
  );
};

export default TeamTable;
